import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { FileXls } from '@phosphor-icons/react';
import { formatDate } from '../utils/dateFormat';
import { ProcurementJobCard, ApprovalDialog } from '../components/ProcurementComponents';
import { SearchFilterBar, EmptySearchResult, LoadErrorState } from '../components/SearchFilterBar';

const API_URL = '';

function getAuthFileUrl(storagePath) {
  // httpOnly cookies are sent automatically for same-origin requests
  return `${API_URL}/api/files/${storagePath}`;
}

const PROCUREMENT_SEARCH_FIELDS = [
  { key: 'jo_number', label: 'JO Number', placeholder: 'Enter JO number' },
  { key: 'customer_name', label: 'Customer Name', placeholder: 'Enter customer name' },
  { key: 'customer_mobile', label: 'Customer Mobile', placeholder: 'Enter mobile number' },
];

const PROCUREMENT_DEFAULT_FILTERS = { jo_number: '', customer_name: '', customer_mobile: '' };

export function Procurement() {
  const [jobOrders, setJobOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [filterColour, setFilterColour] = useState(false);
  const [filterSpecialGlass, setFilterSpecialGlass] = useState(false);
  const [filters, setFilters] = useState(PROCUREMENT_DEFAULT_FILTERS);
  const [loadError, setLoadError] = useState(false);

  const fetchPendingJobs = useCallback(async (searchFilters) => {
    setLoading(true);
    setLoadError(false);
    try {
      const f = searchFilters || PROCUREMENT_DEFAULT_FILTERS;
      const params = new URLSearchParams({ status: 'pending_procurement', include_files: 'true' });
      Object.entries(f).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      const { data } = await api.get(`/api/job-orders?${params}`);
      setJobOrders(data);
    } catch (err) {
      if (err?.isAuthError) return;
      setLoadError(true);
      toast.error('Failed to load pending jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPendingJobs(); }, [fetchPendingJobs]);

  const handleSearch = () => { fetchPendingJobs(filters); };
  const handleReset = (cleared) => { setFilters(cleared); fetchPendingJobs(cleared); };
  const handleRefresh = () => { fetchPendingJobs(filters); };

  const handleApproval = async (jo_number, status) => {
    setActionLoading(true);
    try {
      await api.post(
        `/api/job-orders/${jo_number}/procurement-approval`,
        { status, remarks },
        { withCredentials: true }
      );
      toast.success(`Job Order ${status === 'approved' ? 'Approved' : 'Rejected'}`);
      setSelectedJob(null);
      setRemarks('');
      fetchPendingJobs();
    } catch (error) {
      toast.error('Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const viewFiles = (files) => {
    if (!files || files.length === 0) {
      toast.info('No files uploaded');
      return;
    }
    files.forEach((file, index) => {
      // Object Store files: open via authenticated URL
      if (file.storage_path) {
        window.open(getAuthFileUrl(file.storage_path), `_blank_${index}`);
        return;
      }
      // Legacy base64 files
      const dataUrl = file.data?.startsWith?.('data:') ? file.data : `data:${file.content_type};base64,${file.data}`;
      if (!dataUrl || dataUrl === 'data:undefined;base64,undefined') {
        toast.error(`Unable to open ${file.filename || 'attachment'} — file data is missing`);
        return;
      }
      const newWindow = window.open('', `_blank_${index}`);
      if (newWindow) {
        const html = newWindow.document.createElement('html');
        const head = newWindow.document.createElement('head');
        const title = newWindow.document.createElement('title');
        const body = newWindow.document.createElement('body');
        title.textContent = file.filename;
        head.appendChild(title);
        if (file.content_type === 'application/pdf') {
          body.style.margin = '0';
          const embed = newWindow.document.createElement('embed');
          embed.src = dataUrl;
          embed.type = 'application/pdf';
          embed.width = '100%';
          embed.height = '100%';
          body.appendChild(embed);
        } else {
          body.style.cssText = 'margin:0;display:flex;justify-content:center;align-items:center;background:#000;';
          const img = newWindow.document.createElement('img');
          img.src = dataUrl;
          img.style.cssText = 'max-width:100%;max-height:100vh;';
          body.appendChild(img);
        }
        html.appendChild(head);
        html.appendChild(body);
        newWindow.document.documentElement.replaceWith(html);
      }
    });
    toast.success(`Opened ${files.length} file(s)`);
  };

  // Apply local filters
  let filteredOrders = jobOrders;
  if (filterColour) {
    filteredOrders = filteredOrders.filter(jo => jo.profile_type === 'Colour');
  }
  if (filterSpecialGlass) {
    filteredOrders = filteredOrders.filter(jo => jo.glass_type !== 'Normal');
  }

  // Compute totals
  const totalWindows = filteredOrders.reduce((sum, jo) => sum + (jo.no_of_windows || 0), 0);
  const totalSqft = filteredOrders.reduce((sum, jo) => sum + (jo.total_sqft || 0), 0);

  const exportToCSV = () => {
    const headers = ['JO No', 'Customer', 'Mobile', 'Location', 'Windows', 'Sqft', 'Delivery Date', 'Profile Type', 'Glass Type', 'Executive'];
    const rows = filteredOrders.map(jo => [
      jo.jo_number, jo.customer_name, jo.customer_mobile, jo.work_location,
      jo.no_of_windows, jo.total_sqft, jo.delivery_date, jo.profile_type, jo.glass_type, jo.executive_name
    ]);
    let csv = headers.join(',') + '\n';
    rows.forEach(row => { csv += row.map(cell => `"${cell}"`).join(',') + '\n'; });
    csv += `\n"TOTALS","","","","${totalWindows}","${totalSqft}","","","",""`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `procurement_pending_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success(`Exported ${filteredOrders.length} records`);
  };

  const isSpecial = (jo) => jo.profile_type === 'Colour' || jo.glass_type !== 'Normal';

  return (
    <Layout>
      <div className="space-y-6 pb-20">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-light tracking-tight text-zinc-950">Procurement Approval</h1>
            <p className="text-sm text-zinc-500 mt-2">Review and approve pending job orders</p>
          </div>
          <Button
            data-testid="procurement-export-button"
            onClick={exportToCSV}
            variant="outline"
            className="h-11"
          >
            <FileXls size={20} weight="bold" className="mr-2" />
            Export Filtered ({filteredOrders.length})
          </Button>
        </div>

        <SearchFilterBar
          fields={PROCUREMENT_SEARCH_FIELDS}
          filters={filters}
          setFilters={setFilters}
          onSearch={handleSearch}
          onReset={handleReset}
          onRefresh={handleRefresh}
          loading={loading}
          resultCount={filteredOrders.length}
        />

        {/* Highlight Filters */}
        <Card className="border-zinc-300 rounded-sm shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-6 flex-wrap">
              <Label className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Highlight Filters:</Label>
              <div className="flex items-center gap-2">
                <Checkbox
                  data-testid="filter-colour-profile"
                  checked={filterColour}
                  onCheckedChange={setFilterColour}
                />
                <label className="text-sm font-bold text-orange-700">Colour Profile Only</label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  data-testid="filter-special-glass"
                  checked={filterSpecialGlass}
                  onCheckedChange={setFilterSpecialGlass}
                />
                <label className="text-sm font-bold text-blue-700">Special Glass Only</label>
              </div>
            </div>
          </CardContent>
        </Card>

        {(() => {
          if (loading) return <div className="text-center py-12">Loading pending job orders...</div>;
          if (loadError) return <LoadErrorState onRetry={handleRefresh} />;
          if (filteredOrders.length === 0) {
            return <EmptySearchResult onReset={() => handleReset(PROCUREMENT_DEFAULT_FILTERS)} />;
          }
          return (
          <>
            <div className="space-y-4">
              {filteredOrders.map((jo) => (
                <ProcurementJobCard
                  key={jo.jo_number}
                  jo={jo}
                  isSpecial={isSpecial(jo)}
                  onViewFiles={viewFiles}
                  onApprove={(j) => { setSelectedJob(j); setRemarks(''); }}
                  onReject={(j) => { setSelectedJob({ ...j, action: 'reject' }); setRemarks(''); }}
                />
              ))}
            </div>

            {/* Totals Bar */}
            <Card className="border-zinc-300 rounded-sm shadow-sm bg-zinc-50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <p className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Totals ({filteredOrders.length} orders)</p>
                  <div className="flex gap-8">
                    <div className="text-center">
                      <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Windows</p>
                      <p data-testid="procurement-total-windows" className="text-2xl font-bold text-zinc-950">{totalWindows}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total Sqft</p>
                      <p data-testid="procurement-total-sqft" className="text-2xl font-bold text-zinc-950">{totalSqft.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
          );
        })()}
      </div>

      <ApprovalDialog
        selectedJob={selectedJob}
        onClose={() => setSelectedJob(null)}
        remarks={remarks}
        setRemarks={setRemarks}
        onConfirm={handleApproval}
        loading={actionLoading}
      />
    </Layout>
  );
}
